<template>
  <nav :class="['navbar', { scrolled: isScrolled }]">
    <div class="nav-links">
      <a href="#hero" class="nav-link" ref="heroRef">
        <font-awesome-icon v-if="isMobile" :icon="['fas', 'home']" />
        <span v-else>{{ $t("nav.home") }}</span>
      </a>
      <a href="#about" class="nav-link" ref="aboutRef">
        <font-awesome-icon v-if="isMobile" :icon="['fas', 'user']" />
        <span v-else>{{ $t("nav.about") }}</span>
      </a>
      <a href="#skills" class="nav-link" ref="skillsRef">
        <font-awesome-icon v-if="isMobile" :icon="['fas', 'tools']" />
        <span v-else>{{ $t("nav.skills") }}</span>
      </a>
      <a href="#experience" class="nav-link" ref="experienceRef">
        <font-awesome-icon v-if="isMobile" :icon="['fas', 'briefcase']" />
        <span v-else>{{ $t("nav.experience") }}</span>
      </a>
      <a href="#projects" class="nav-link" ref="projectsRef">
        <font-awesome-icon v-if="isMobile" :icon="['fas', 'tasks']" />
        <span v-else>{{ $t("nav.projects") }}</span>
      </a>
      <a href="#contact" class="nav-link" ref="contactRef">
        <font-awesome-icon v-if="isMobile" :icon="['fas', 'envelope']" />
        <span v-else>{{ $t("nav.contact") }}</span>
      </a>
    </div>
    <div class="nav-actions">
      <button @click="toggleDarkMode" class="nav-button" ref="darkModeRef">
        <font-awesome-icon :icon="['fas', 'moon']" class="nav-icon" />
      </button>
      <button @click="toggleLanguage" class="nav-button" ref="languageRef">
        <font-awesome-icon :icon="['fas', 'language']" class="nav-icon" />
      </button>
    </div>
  </nav>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

export default {
  name: "NavbarComponent",
  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const isScrolled = ref(false);
    const isMobile = ref(window.innerWidth <= 768);

    const tooltips = ref([]);
    const heroRef = ref(null);
    const aboutRef = ref(null);
    const skillsRef = ref(null);
    const experienceRef = ref(null);
    const projectsRef = ref(null);
    const contactRef = ref(null);
    const darkModeRef = ref(null);
    const languageRef = ref(null);

    const checkScroll = () => {
      isScrolled.value = window.scrollY > 10;
    };

    const checkScreenSize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onMounted(() => {
      window.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScreenSize);

      initTooltips();
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScreenSize);
    });

    const toggleDarkMode = () => {
      store.dispatch("toggleDarkMode");
    };

    const toggleLanguage = () => {
      locale.value = locale.value === "es" ? "en" : "es";
    };

    const initTooltips = () => {
      tooltips.value.forEach((t) => t.destroy()); // Eliminar tooltips previos
      tooltips.value = [];

      if (!isMobile.value) return;

      const tooltipOptions = [
        { ref: heroRef, content: t("nav.home") },
        { ref: aboutRef, content: t("nav.about") },
        { ref: skillsRef, content: t("nav.skills") },
        { ref: projectsRef, content: t("nav.projects") },
        { ref: experienceRef, content: t("nav.experience") },
        { ref: contactRef, content: t("nav.contact") },
        { ref: darkModeRef, content: t("nav.darkMode") },
        { ref: languageRef, content: t("nav.language") },
      ];

      tooltipOptions.forEach(({ ref, content }) => {
        if (ref.value) {
          tooltips.value.push(
            tippy(ref.value, { content, placement: "bottom" })
          );
        }
      });
    };

    watch(locale, () => {
      initTooltips();
    });

    watch(isMobile, () => {
      initTooltips();
    });

    return {
      t,
      toggleDarkMode,
      toggleLanguage,
      isScrolled,
      isMobile,
      heroRef,
      aboutRef,
      skillsRef,
      experienceRef,
      projectsRef,
      contactRef,
      darkModeRef,
      languageRef,
    };
  },
};
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  box-shadow: none;
  border: none;
  z-index: 1000;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.navbar.scrolled {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.2);
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 30px;
}

.nav-links {
  display: flex;
  gap: 20px;
}

.navbar:not(.scrolled) .nav-link,
.navbar:not(.scrolled) .nav-icon {
  color: #fff;
}
.nav-link {
  text-decoration: none;
  color: #333;
  font-size: 0.9rem;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.nav-link:hover {
  color: #42b983;
}

.nav-link span {
  white-space: nowrap;
}

.nav-actions {
  display: flex;
  gap: 5px;
  margin-left: 15px;
}

.nav-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s;
}

.nav-button:hover {
  transform: scale(1.1);
}

.nav-icon {
  font-size: 1rem;
  color: #333;
}

/* Estilos para el modo nocturno */
.dark-mode .navbar.scrolled {
  background-color: rgba(42, 42, 64, 0.9);
}

.dark-mode .nav-link {
  color: #f0f0f0;
}

.dark-mode .nav-link:hover {
  color: #42b983;
}

.dark-mode .nav-icon {
  color: #f0f0f0;
}

.dark-mode .nav-button:hover .nav-icon {
  color: #42b983;
}

@media (max-width: 870px) {
  .nav-link span {
    font-size: 0.8rem;
  }
}

@media (max-width: 820px) {
  .nav-link span {
    font-size: 0.75rem;
  }
}

/* RESPONSIVE: Ocultar texto en móviles */
@media (max-width: 768px) {
  .nav-link span {
    display: none;
  }

  .nav-link {
    font-size: 1.2rem;
  }
}
</style>
